/* eslint-disable jsx-a11y/no-autofocus */
import React, {
  useEffect, useMemo, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import Popover from '@mui/material/Popover';
// eslint-disable-next-line import/no-extraneous-dependencies
import {
  Globe, ChevronDown, Search, Check,
} from 'lucide-react';
import { filterTimezoneOptions, findTimezoneOption } from '../../utils/timezones';

// Searchable single-select over the full IANA timezone list. Options are titled
// "Zone — Country" and search matches the zone, the country (including the other
// countries that share a zone), the abbreviation and the GMT offset.
const panelPaperSx = {
  marginTop: '4px',
  maxHeight: 340,
  border: '1px solid #e0e1e6',
  borderRadius: '8px',
  boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -4px rgba(0, 0, 0, 0.1)',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
};

const triggerBase = 'w-full box-border inline-flex items-center gap-2 h-10 px-3 border rounded-[8px] '
  + 'text-sm bg-white text-ink-900 transition text-left';

function TimezonePicker({
  value, onChange, error, disabled, placeholder, id,
}) {
  const triggerRef = useRef(null);
  const listRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);

  const selected = useMemo(() => findTimezoneOption(value), [value]);
  const options = useMemo(() => filterTimezoneOptions(query), [query]);

  useEffect(() => {
    if (!open) return;
    const index = options.findIndex((option) => option.zone === value);
    setActiveIndex(index >= 0 ? index : 0);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const active = listRef.current?.children?.[activeIndex];
    if (active?.scrollIntoView) active.scrollIntoView({ block: 'nearest' });
  }, [activeIndex, options]);

  const closePanel = () => {
    setOpen(false);
    setQuery('');
  };

  const select = (zone) => {
    onChange(zone);
    closePanel();
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      if (!options.length) return;
      const next = e.key === 'ArrowDown' ? activeIndex + 1 : activeIndex - 1;
      setActiveIndex((next + options.length) % options.length);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (options[activeIndex]) select(options[activeIndex].zone);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      closePanel();
    }
  };

  const borderCls = () => {
    if (error) return 'border-[#CC4525]';
    if (open) return 'border-brand-500';
    return 'border-rule hover:border-brand-500';
  };

  return (
    <>
      <button
        id={id}
        ref={triggerRef}
        type="button"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen(true)}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown') { e.preventDefault(); setOpen(true); }
        }}
        className={`${triggerBase} ${borderCls()} ${disabled ? 'opacity-60' : 'cursor-pointer'}`}
        style={{ borderStyle: 'solid' }}
      >
        <Globe size={14} className="text-brand-500 flex-shrink-0" />
        {selected ? (
          <span className="flex-1 min-w-0 truncate">
            {selected.zone}
            {selected.country
              ? <span className="text-ink-500">{` — ${selected.country}`}</span>
              : null}
          </span>
        ) : (
          <span className="flex-1 min-w-0 truncate text-ink-500">{placeholder}</span>
        )}
        <ChevronDown
          size={12}
          className={`text-ink-500 flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>

      <Popover
        open={open}
        anchorEl={triggerRef.current}
        onClose={closePanel}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{ sx: { ...panelPaperSx, width: triggerRef.current?.offsetWidth } }}
      >
        <div
          className="flex items-center gap-2 px-3 h-10 border-b border-rule flex-shrink-0"
          style={{ borderBottomStyle: 'solid' }}
        >
          <Search size={14} className="text-ink-500 flex-shrink-0" />
          <input
            autoFocus
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setActiveIndex(0);
            }}
            onKeyDown={handleSearchKeyDown}
            placeholder="Search timezone or country"
            className={'flex-1 min-w-0 h-full text-sm text-ink-900 placeholder:text-ink-500 '
              + 'border-0 focus:outline-none'}
          />
        </div>

        {options.length ? (
          <div ref={listRef} role="listbox" aria-label="Timezone" className="overflow-y-auto p-1">
            {options.map((option, index) => {
              const isSelected = option.zone === selected?.zone;
              const isActive = index === activeIndex;
              let rowBg = '';
              if (isSelected) rowBg = 'bg-[#e8eef7]';
              else if (isActive) rowBg = 'bg-surface-page';
              return (
                <button
                  key={option.zone}
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  onMouseEnter={() => setActiveIndex(index)}
                  onClick={() => select(option.zone)}
                  className={'w-full flex items-center gap-2 text-left px-2.5 py-1.5 '
                    + `rounded-[6px] transition ${rowBg}`}
                >
                  <span className="flex-1 min-w-0">
                    <span className={`block text-sm truncate ${
                      isSelected ? 'text-brand-700 font-medium' : 'text-ink-900'
                    }`}
                    >
                      {option.label}
                    </span>
                    <span className="block text-xs text-ink-500 truncate">
                      {option.abbrLabel
                        ? `${option.offsetLabel} · ${option.abbrLabel}`
                        : option.offsetLabel}
                      {option.otherCountries.length ? ` · +${option.otherCountries.length} more` : ''}
                    </span>
                  </span>
                  {isSelected
                    ? <Check size={14} className="text-brand-700 flex-shrink-0" />
                    : null}
                </button>
              );
            })}
          </div>
        ) : (
          <p className="text-sm text-ink-500 px-3 py-4 m-0">No timezones found</p>
        )}
      </Popover>
    </>
  );
}

TimezonePicker.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  error: PropTypes.bool,
  disabled: PropTypes.bool,
  placeholder: PropTypes.string,
  id: PropTypes.string,
};

TimezonePicker.defaultProps = {
  value: '',
  error: false,
  disabled: false,
  placeholder: 'Select timezone',
  id: undefined,
};

export default TimezonePicker;
